import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconThreeDots } from '~/ui/core/icons';

import { useJobArchitecturePermissions } from '../../context/JobArchitecturePermissionsContext';
import { CommentMessage } from './CommentMessage';
import type { HiddenCommentGroup } from './comment-classification';
import { formatRelativeTime } from './format-relative-time';
import type { Comment, CommentAuthor } from './types';
import { useCurrentCommentAuthor } from './use-current-comment-author';
import { useDeleteComment } from './use-delete-comment';
import { useRepliesQuery } from './use-replies-query';
import { useResolveComment } from './use-resolve-comment';

type HiddenCommentsListProps = {
    readonly architectureId: string;
    readonly commentResolutionDisabled?: boolean;
    readonly groups: readonly HiddenCommentGroup[];
    readonly itemId: string;
    readonly language: string;
    readonly version: number;
};

type HiddenCommentCardProps = {
    readonly architectureId: string;
    readonly comment: Comment;
    readonly commentResolutionDisabled: boolean;
    readonly itemId: string;
    readonly language: string;
    readonly version: number;
};

type HiddenCommentActionsProps = {
    readonly canDelete: boolean;
    readonly canResolve: boolean;
    readonly deletePending: boolean;
    readonly commentResolutionDisabled: boolean;
    readonly onDelete: () => void;
    readonly onResolve: () => void;
    readonly resolvePending: boolean;
};

const sortByCreatedAtAscending = (first: Comment, second: Comment): number =>
    first.createdAt.localeCompare(second.createdAt);

const normalizeQuote = (quote: string | undefined): string | null => {
    if (is.undefined(quote)) return null;
    const normalized = quote.replace(/\s+/g, ' ').trim();
    return is.nonEmptyString(normalized) ? normalized : null;
};

const formatSourceLanguageCode = (language: string): string => {
    const [primarySubtag] = language.trim().split(/[-_]/u);
    return is.nonEmptyString(primarySubtag) ? primarySubtag.toLowerCase() : '';
};

const deriveDisplayName = (author: CommentAuthor | null | undefined, fallback: string): string => {
    if (is.nullOrUndefined(author)) return fallback;
    const parts = [author.firstName, author.lastName].map((part) => part.trim()).filter((part) => part.length > 0);
    if (parts.length === 0) return fallback;
    return parts.join(' ');
};

const ReplyList: FunctionComponent<{ readonly replies: readonly Comment[] }> = ({ replies }) =>
    replies.length > 0 ? (
        <div className="border-t border-gray-200/80 pt-m" data-testid="hidden-comment-replies">
            <div className="flex flex-col gap-xl">
                {replies.map((reply) => (
                    <div key={reply.id} data-testid="hidden-comment-reply">
                        <CommentMessage author={reply.author} content={reply.content} createdAt={reply.createdAt} />
                    </div>
                ))}
            </div>
        </div>
    ) : null;

const HiddenCommentActions: FunctionComponent<HiddenCommentActionsProps> = ({
    canDelete,
    canResolve,
    commentResolutionDisabled,
    deletePending,
    onDelete,
    onResolve,
    resolvePending,
}) => {
    const translate = useTranslate();
    if (!canDelete && !canResolve) return null;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger>
                <Button
                    aria-label={translate('common.more')}
                    className="pointer-events-none opacity-0 transition-opacity duration-150 ease-out group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover/comment:pointer-events-auto group-hover/comment:opacity-100 group-focus-within/comment:pointer-events-auto group-focus-within/comment:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100"
                    iconOnly={true}
                    size="sm"
                    type="button"
                    variant="tertiary"
                >
                    <IconThreeDots />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                {canResolve ? (
                    <DropdownMenuItem
                        data-testid="hidden-comment-resolve"
                        disabled={commentResolutionDisabled || resolvePending}
                        onSelect={onResolve}
                        tooltipContent={
                            commentResolutionDisabled
                                ? translate('pages.job-architecture-v2.comments.thread.resolve-disabled-edit-mode')
                                : undefined
                        }
                    >
                        {translate('pages.job-architecture-v2.comments.thread.resolve')}
                    </DropdownMenuItem>
                ) : null}
                {canDelete ? (
                    <DropdownMenuItem
                        className="hover:!text-error-600"
                        data-testid="hidden-comment-delete"
                        disabled={deletePending}
                        onSelect={onDelete}
                    >
                        {translate('pages.job-architecture-v2.comments.thread.delete')}
                    </DropdownMenuItem>
                ) : null}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const HiddenCommentCard: FunctionComponent<HiddenCommentCardProps> = ({
    architectureId,
    comment,
    commentResolutionDisabled,
    itemId,
    language,
    version,
}) => {
    const translate = useTranslate();
    const { canRead } = useJobArchitecturePermissions();
    const currentAuthor = useCurrentCommentAuthor();
    const deleteComment = useDeleteComment({ architectureId, itemId, language, version });
    const resolveComment = useResolveComment({ architectureId, itemId, language, version });
    const [confirmOpen, setConfirmOpen] = useState(false);
    const { data: replies } = useRepliesQuery({
        architectureId,
        itemId,
        parentCommentId: comment.id,
        version,
    });
    const sortedReplies = [...(replies ?? [])].sort(sortByCreatedAtAscending);
    const isCrossLanguage = comment.sourceLanguage !== language;
    const sourceLanguageLabel = formatSourceLanguageCode(comment.sourceLanguage);
    const quote =
        comment.target.kind === 'description-section' ? normalizeQuote(comment.anchor?.quoteSelector.exact) : null;
    const canDelete = comment.author.id === currentAuthor.id;
    const canResolve = canRead && is.nullOrUndefined(comment.resolution);
    const resolvedBy = deriveDisplayName(
        comment.resolution?.resolvedBy,
        translate('pages.job-architecture-v2.comments.thread.unknown-author'),
    );

    const handleDelete = (): void => {
        setConfirmOpen(false);
        if (deleteComment.isPending) return;
        deleteComment.mutate({ commentId: comment.id });
    };

    const handleResolve = (): void => {
        if (commentResolutionDisabled || resolveComment.isPending) return;
        resolveComment.mutate({ commentId: comment.id });
    };

    return (
        <div
            className="group group/comment relative overflow-hidden rounded-[calc(var(--radius-md)+var(--spacing-s))] bg-white-200 shadow-sm transition-shadow duration-200 ease-[cubic-bezier(.165,.84,.44,1)] hover:shadow-md"
            data-testid="hidden-comment-card"
        >
            <div className="flex flex-col p-xl">
                {is.nonEmptyString(quote) ? (
                    <blockquote
                        className="m-0 mb-m min-w-0 border-l-2 border-gray-200 pl-xs text-xs leading-5 text-gray-800"
                        data-testid="hidden-comment-origin-quote"
                        title={quote}
                    >
                        {quote}
                    </blockquote>
                ) : null}
                {!is.nullOrUndefined(comment.resolution) ? (
                    <p
                        className="m-0 mb-m min-w-0 text-xxs font-medium leading-[1rem] text-gray-700"
                        data-testid="hidden-comment-resolution"
                    >
                        {translate('pages.job-architecture-v2.comments.thread.resolved-by', {
                            time: formatRelativeTime(comment.resolution.resolvedAt),
                            user: resolvedBy,
                        })}
                    </p>
                ) : null}
                <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-s">
                    <div className="min-w-0">
                        <CommentMessage
                            author={comment.author}
                            content={comment.content}
                            createdAt={comment.createdAt}
                        />
                    </div>
                    <div className="flex shrink-0 items-center gap-xs">
                        {isCrossLanguage ? (
                            <span
                                className="text-xxs font-medium leading-[1rem] text-gray-700"
                                data-testid="hidden-comment-source-language-code"
                            >
                                {sourceLanguageLabel}
                            </span>
                        ) : null}
                        <HiddenCommentActions
                            canDelete={canDelete}
                            canResolve={canResolve}
                            commentResolutionDisabled={commentResolutionDisabled}
                            deletePending={deleteComment.isPending}
                            resolvePending={resolveComment.isPending}
                            onDelete={() => setConfirmOpen(true)}
                            onResolve={handleResolve}
                        />
                    </div>
                </div>
                <ReplyList replies={sortedReplies} />
            </div>
            <ConfirmationDialog
                cancelButtonLabel={translate('common.cancel')}
                confirmButtonLabel={translate('common.delete')}
                confirmButtonVariant="destructive"
                description={translate('pages.job-architecture-v2.comments.thread.delete-confirm-description')}
                open={confirmOpen}
                title={translate('pages.job-architecture-v2.comments.thread.delete-confirm-title')}
                onCancel={() => setConfirmOpen(false)}
                onConfirm={handleDelete}
            />
        </div>
    );
};

export const HiddenCommentsList: FunctionComponent<HiddenCommentsListProps> = ({
    architectureId,
    commentResolutionDisabled = false,
    groups,
    itemId,
    language,
    version,
}) => {
    return (
        <div
            className="flex min-h-full flex-1 flex-col gap-xl overflow-y-auto px-s py-m"
            data-testid="hidden-comments-list"
        >
            {groups.map((group) => (
                <section key={group.id} className="flex flex-col gap-s" data-testid="hidden-comment-group">
                    <h3 className="m-0 px-xs text-xxs font-medium leading-[1rem] tracking-tight text-gray-700">
                        {group.label}
                    </h3>
                    <div className="flex flex-col gap-s">
                        {group.comments.map((comment) => (
                            <HiddenCommentCard
                                key={comment.id}
                                architectureId={architectureId}
                                comment={comment}
                                commentResolutionDisabled={commentResolutionDisabled}
                                itemId={itemId}
                                language={language}
                                version={version}
                            />
                        ))}
                    </div>
                </section>
            ))}
        </div>
    );
};
